import { deserializeJsonApiData } from "Data/Utils/Json";
import { IOrder, IWithApiData } from "Interfaces";
import * as ItemsApi from "Data/API/Items";
import { setItemsOptionsAction } from "Data/Redux/Slices/Items";
import { createAsyncThunk } from "Data/Redux/Store";
import { selectItems, selectFilteredItems, selectItemsOptions } from "Data/Redux/Slices/Items";
import { NotificationType, notify } from "src/Notifications";
import React from "react";
import { isEqual } from "lodash";
import { defaultPaginatedStateOptions } from "Data/Objects/Redux";
import { selectAllFilters, selectSiderFilters } from "Data/Redux/Slices/Ui";
import { selectToken } from "Data/Redux/Slices/User";

const notificationContent = (thunkApi) => {
	const resetTableFetching = () => thunkApi.dispatch(
		setItemsOptionsAction({
			...selectItemsOptions(thunkApi.getState()),
			"page[number]": 1
		})
	);

	const anchor = React.createElement(
		"a",
		{ onClick: resetTableFetching, key: "refresh-table", className: "selectable-text" },
		"here"
	);

	return React.createElement("div", null, [ "Click ", anchor, " to refresh table and get new content." ]);
};

export const createOrderAndFetchItems = createAsyncThunk(
	"pusher/createOrderAndFetchItems",
	async (order: IWithApiData, thunkApi) => {
		const authToken = selectToken(thunkApi.getState());
		const itemOptions = selectItemsOptions(thunkApi.getState());

		// do nothing if no filters are applied
		if (isEqual(itemOptions, defaultPaginatedStateOptions)) {
			return;
		}

		return deserializeJsonApiData<Partial<IOrder>>(order).then((deserialized: Partial<IOrder>) => {
			const orderArray = Array.isArray(deserialized) ? deserialized : [ deserialized ];

			return Promise
				.all(orderArray.map((currentOrder) => {
					return ItemsApi.getItems(
						authToken,
						{ "filter[order]": currentOrder.orderNumber }
					).then((response) => {
						return response.data;
					});
				}))
				.then((unflattenedItems) => {
					const state = thunkApi.getState();
					const items = unflattenedItems.flat();
					const filteredItemIds = selectFilteredItems(
						state, selectSiderFilters(state, selectAllFilters(state)), items
					).map((item) => item.id);

					const newData = filteredItemIds.length > 0;

					if (newData) {
						notify(
							NotificationType.INFO,
							"New Data Matching Filters",
							notificationContent(thunkApi),
							false,
							{
								position: "bottom-right",
								autoClose: false,
								toastId: "new-table-data",
								className: "new-table-data-notification"
							}
						);
					}
				});
		});
	}
);

export const updateOrderAndFilterItems = createAsyncThunk(
	"pusher/updateOrderAndFilterItems",
	async (order: IWithApiData, thunkApi) => {
		const itemOptions = selectItemsOptions(thunkApi.getState());

		// do nothing if no filters are applied
		if (isEqual(itemOptions, defaultPaginatedStateOptions)) {
			return;
		}

		return deserializeJsonApiData<Partial<IOrder>>(order).then((deserialized: Partial<IOrder>) => {
			const orderArray = Array.isArray(deserialized) ? deserialized : [ deserialized ];
			const orderIds = orderArray.map((currentOrder) => currentOrder.id);
			const authToken = selectToken(thunkApi.getState());

			return Promise
				.all(orderArray.map((currentOrder) => {
					return ItemsApi.getItems(
						authToken,
						{ "filter[order]": currentOrder.orderNumber }
					).then((response) => {
						return response.data;
					});
				}))
				.then((unflattenedItems) => {
					const state = thunkApi.getState();
					const items = unflattenedItems.flat();
					const relevantStateItems = selectItems(state).filter((item) => {
						return orderIds.includes(item?.order?.id);
					}).map((item) => item.id).sort();

					const filteredItemIds = selectFilteredItems(
						state, selectSiderFilters(state, selectAllFilters(state)), items
					).map((item) => item.id).sort();

					const newData = filteredItemIds.length > 0 && !isEqual(relevantStateItems, filteredItemIds);

					if (newData) {
						notify(
							NotificationType.INFO,
							"New Data Matching Filters",
							notificationContent(thunkApi),
							false,
							{
								position: "bottom-right",
								autoClose: false,
								toastId: "new-table-data",
								className: "new-table-data-notification"
							}
						);
					}
				});
		});
	}
);

export const deleteOrderLocal = createAsyncThunk(
	"pusher/deleteOrder",
	async (id: string, thunkApi) => {
		const newData = selectItems(thunkApi.getState()).some((item) => {
			return item?.order?.id === id;
		});

		if (newData) {
			notify(
				NotificationType.INFO,
				"New Data Matching Filters",
				notificationContent(thunkApi),
				false,
				{
					position: "bottom-right",
					autoClose: false,
					toastId: "new-table-data",
					className: "new-table-data-notification"
				}
			);
		}
	}
);
